import { useEffect, useState } from "react";
import { cable } from "./cable.js";
import type { AuthSession } from "@/auth/keycloak.js";

/** One person online in the realm. `since` is when their FIRST tab connected, so a second tab doesn't reset it. */
export interface PresentUser {
  userId: string;
  name: string;
  since: string;
  connections: number;
}

/**
 * Who is online in this realm, live (ADR-026 Phase 5).
 *
 * **No subscribe call.** Every authenticated socket is placed in its realm's presence room by the server, because "who
 * is online in my realm" is exactly the set that socket already belongs to — asking the client to request it would let
 * it name a room, which is the one thing room membership must never be driven by.
 *
 * The server pushes the whole roster on every change rather than deltas: it is a handful of rows, and a full snapshot
 * cannot drift out of sync the way an accumulated stream of joins/leaves can after a dropped message or a reconnect.
 */
export function usePresence(session: AuthSession | null): PresentUser[] {
  const [users, setUsers] = useState<PresentUser[]>([]);

  useEffect(() => {
    if (!session) return;
    const onPresence = (payload: { users: PresentUser[] }): void => setUsers(payload.users ?? []);
    const s = cable();
    s.on("app:presence", onPresence);
    return () => {
      s.off("app:presence", onPresence);
    };
  }, [session]);

  return users;
}

/** "3m", "2h", "4d" — a compact age for "online since", chosen so the header stays a glance, not a timestamp to read. */
export function shortAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "now";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
